import { useState } from 'react';
import { Input, Button } from '@/components/common';
import type { ReferidoFormData } from '@/types';

interface PhaseNamesProps {
  referidos: ReferidoFormData[];
  onAddReferido: (nombre: string, apellido: string) => void;
  onRemoveReferido: (index: number) => void;
  onContinue: () => void;
}

export function PhaseNames({
  referidos,
  onAddReferido,
  onRemoveReferido,
  onContinue,
}: PhaseNamesProps) {
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleAdd = () => {
    const newErrors: Record<string, string> = {};

    // Only nombre is required
    if (!nombre.trim() || nombre.trim().length < 2) {
      newErrors.nombre = 'Mínimo 2 caracteres';
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    onAddReferido(nombre.trim(), apellido.trim());
    setNombre('');
    setApellido('');
    setErrors({});
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-gray-900">
          Solo pide nombres
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          No pidas teléfonos aún. Solo nombres para que no filtren.
        </p>
      </div>

      {/* Add name form */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Nombre"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            onKeyDown={handleKeyDown}
            error={errors.nombre}
            placeholder="Ej: Ana"
            required
          />
          <Input
            label="Apellido"
            value={apellido}
            onChange={(e) => setApellido(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Opcional"
          />
        </div>

        <Button
          variant="outline"
          onClick={handleAdd}
          className="w-full"
        >
          + Agregar Nombre
        </Button>
      </div>

      {/* List of names */}
      {referidos.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {referidos.length} nombre{referidos.length !== 1 ? 's' : ''} agregado{referidos.length !== 1 ? 's' : ''}
          </p>
          <div className="flex flex-wrap gap-2">
            {referidos.map((ref, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-700 pl-3 pr-1.5 py-1.5 rounded-full text-sm font-medium"
              >
                {ref.nombre}{ref.apellido ? ` ${ref.apellido}` : ''}
                <button
                  type="button"
                  onClick={() => onRemoveReferido(index)}
                  className="w-5 h-5 rounded-full flex items-center justify-center hover:bg-primary-200 transition-colors"
                  aria-label={`Eliminar ${ref.nombre}`}
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Continue button */}
      {referidos.length > 0 && (
        <Button onClick={onContinue} className="w-full">
          Continuar a Mensajes ({referidos.length})
        </Button>
      )}

      {referidos.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-4">
          Agrega al menos un nombre para continuar
        </p>
      )}
    </div>
  );
}
